import { useCallback } from 'react'
import { useUi, type Locale } from '@/stores/ui'
import { ar } from './ar'
import { en, type MessageKey } from './en'

const dictionaries: Record<Locale, Record<MessageKey, string>> = { en, ar }

export type { MessageKey }
export type Localized = { en: string; ar: string }

function format(template: string, vars?: Record<string, string | number>) {
  return vars ? template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`)) : template
}

export function translate(locale: Locale, key: MessageKey, vars?: Record<string, string | number>) {
  return format(dictionaries[locale][key] ?? en[key] ?? key, vars)
}

export function useT() {
  const locale = useUi((s) => s.locale)
  const t = useCallback((key: MessageKey, vars?: Record<string, string | number>) => translate(locale, key, vars), [locale])
  /** Picks the right side of a `{ en, ar }` label. */
  const l = useCallback((label: Localized | string | undefined) => (label == null ? '' : typeof label === 'string' ? label : label[locale]), [locale])
  return { t, l, locale, dir: locale === 'ar' ? ('rtl' as const) : ('ltr' as const) }
}

const numberFormatters = new Map<string, Intl.NumberFormat>()
export function formatNumber(value: number | null | undefined, locale: Locale, opts: Intl.NumberFormatOptions = {}) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  const key = `${locale}|${JSON.stringify(opts)}`
  let f = numberFormatters.get(key)
  if (!f) numberFormatters.set(key, (f = new Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-EG', opts)))
  return f.format(value)
}

export const formatMoney = (value: number | null | undefined, locale: Locale) =>
  formatNumber(value, locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
